import type { WaterFallItem } from '@/app/(home-page)/types';
import type {
  WaterFall2Product,
  WaterFall2ResponseItem,
  WaterFall2RuntimeItem,
  WaterFall2StaticPosition,
} from './types';

export const WATERFALL2_PAGE_SIZE = 20;
export const WATERFALL2_PRODUCT_GROUP_SIZE = 5;
const WATERFALL2_SIMILAR_DISABLED_TAIL_SIZE = 2;
const CAMPAIGN_PATH_PATTERN = /\/campaigns\/([^/?#]+)/;

/** 标题行高固定为设计稿的 24px，切换布局或 Tab 时不改变标题区高度。 */
export const getWaterFall2TitlePresentation = (_columns: 1 | 2, configuredColor?: string) => ({
  className: 'font-bb leading-[24px]',
  color: configuredColor || '#282828',
});

const getPagePathname = (pageUrl: string) => {
  if (pageUrl.startsWith('//')) return new URL(`https:${pageUrl}`).pathname;
  if (/^[a-z][a-z\d+.-]*:\/\//i.test(pageUrl)) return new URL(pageUrl).pathname;
  return pageUrl.split(/[?#]/, 1)[0];
};

export const getCampaignSlugFromPageUrl = (pageUrl?: string) => {
  if (!pageUrl) return '';

  try {
    const pathname = getPagePathname(pageUrl);
    const match = pathname.match(CAMPAIGN_PATH_PATTERN);
    return match?.[1] ? decodeURIComponent(match[1]) : '';
  } catch {
    return '';
  }
};

const getProductKey = (product: WaterFall2Product, index: number) =>
  String(product.shop_sku || product.id || product.business_sku || `product-${index}`);

export const normalizeWaterFall2Product = (
  item: WaterFallItem | WaterFall2Product | Record<string, unknown>,
): WaterFall2Product => {
  const source = item as WaterFallItem & WaterFall2Product;
  const nestedProduct = source.product_info as unknown as WaterFall2Product | undefined;

  return {
    ...(nestedProduct || source),
    ...(nestedProduct?.image ? {} : source.image ? { image: source.image } : {}),
  } as WaterFall2Product;
};

export const hasCelebrityImage = (product: WaterFall2Product) =>
  Array.isArray(product.image?.celebrity) && product.image.celebrity.length > 0;

/**
 * 每 5 个商品形成一组。若组首没有红人图，则把组内第一个有红人图的商品前移，
 * 其余商品保持相对顺序；整组没有红人图时不调整。
 */
export const reorderProductsByCelebrity = (products: WaterFall2Product[]) => {
  const result: WaterFall2Product[] = [];

  for (let start = 0; start < products.length; start += WATERFALL2_PRODUCT_GROUP_SIZE) {
    const group = products.slice(start, start + WATERFALL2_PRODUCT_GROUP_SIZE);
    const celebrityIndex = group.findIndex(hasCelebrityImage);

    if (celebrityIndex > 0) {
      const [celebrityProduct] = group.splice(celebrityIndex, 1);
      group.unshift(celebrityProduct);
    }
    result.push(...group);
  }

  return result;
};

export const buildProductRuntimeItems = (
  products: WaterFall2Product[],
  applyCelebrityRule = true,
) =>
  (applyCelebrityRule ? reorderProductsByCelebrity(products) : products).map<WaterFall2RuntimeItem>(
    (product, index) => ({
      key: `product-${getProductKey(product, index)}`,
      kind: 'product',
      product,
      productIndex: index,
      isLarge: applyCelebrityRule && index % WATERFALL2_PRODUCT_GROUP_SIZE === 0,
    }),
  );

export const insertWaterFall2StaticPositions = (
  productItems: WaterFall2RuntimeItem[],
  staticPositions: WaterFall2StaticPosition[],
) => {
  const items = [...productItems];
  const orderedPositions = staticPositions
    .filter((item) => Number.isInteger(item.position) && item.position > 0)
    .sort((left, right) => left.position - right.position);

  orderedPositions.forEach((config) => {
    items.splice(Math.min(config.position - 1, items.length), 0, {
      key: `static-${config.position_type}-${config.id}`,
      kind: 'static',
      config,
    });
  });

  return items;
};

/** 商品接口按 20 条稳定翻页；All Tab 的静态卡按配置坑位插入，不替换或隐藏商品。 */
export const buildWaterFall2ProductAndStaticItems = (
  products: WaterFall2Product[],
  staticPositions: WaterFall2StaticPosition[],
  {
    applyCelebrityRule = true,
  }: {
    applyCelebrityRule?: boolean;
  } = {},
) => {
  const validStaticPositions = staticPositions.filter(
    (item) => Number.isInteger(item.position) && item.position > 0,
  );
  const productItems = buildProductRuntimeItems(products, applyCelebrityRule);
  return insertWaterFall2StaticPositions(productItems, validStaticPositions);
};

export const isWaterFall2StaticPosition = (
  item: WaterFall2ResponseItem,
): item is WaterFall2StaticPosition => item.data_type === 'static_position';

/** 返回每列中下方仍有原始内容（商品卡或静态卡）的坑位 key。 */
export const getKeysWithFollowingColumnItem = (items: Array<{ key: string; column: number }>) => {
  const result = new Set<string>();
  const lastKeyByColumn = new Map<number, string>();

  items.forEach(({ key, column }) => {
    const previousKey = lastKeyByColumn.get(column);
    if (previousKey) result.add(previousKey);
    lastKeyByColumn.set(column, key);
  });

  return result;
};

/** 猜你喜欢要求锚点所在列有后续内容，且锚点不能是当前已加载商品的倒数 2 个。 */
export const canTriggerSimilarRecommendation = ({
  productIndex,
  productCount,
  hasFollowingColumnItem,
}: {
  productIndex: number;
  productCount: number;
  hasFollowingColumnItem: boolean;
}) => hasFollowingColumnItem && productIndex < productCount - WATERFALL2_SIMILAR_DISABLED_TAIL_SIZE;

export interface WaterFall2LayoutPosition {
  column: number;
  x: number;
  y: number;
}

/**
 * 先在没有猜你喜欢的情况下确定原始内容的列归属，再按数组顺序重新堆叠。
 * 中间坑位保持原始列归属；每个已插入的 20 商品组末尾两项按短列收口。
 */
export const buildWaterFall2Layout = ({
  columns,
  items,
  itemHeights,
  itemWidth,
  itemGap,
  columnGap,
  layoutMode = 'logical',
  defaultItemHeight = 240,
}: {
  columns: 1 | 2;
  items: WaterFall2RuntimeItem[];
  itemHeights: Record<string, number>;
  itemWidth: number;
  itemGap: number;
  columnGap: number;
  layoutMode?: 'logical' | 'masonry';
  defaultItemHeight?: number;
}) => {
  const positions = new Map<string, WaterFall2LayoutPosition>();
  const originalColumnHeights = new Array(columns).fill(0) as number[];
  const originalItemColumns: Array<{ key: string; column: number }> = [];
  const originalColumnByKey = new Map<string, number>();
  let originalItemIndex = 0;

  items.forEach((item) => {
    if (item.kind === 'similar') return;

    const column =
      layoutMode === 'masonry'
        ? originalColumnHeights.indexOf(Math.min(...originalColumnHeights))
        : originalItemIndex % columns;
    const y = originalColumnHeights[column];
    originalColumnByKey.set(item.key, column);
    originalItemColumns.push({ key: item.key, column });
    originalColumnHeights[column] = y + (itemHeights[item.key] || defaultItemHeight) + itemGap;
    originalItemIndex += 1;
  });

  const similarItemsByAnchor = new Map<
    string,
    Array<Extract<WaterFall2RuntimeItem, { kind: 'similar' }>>
  >();
  const productItemsByBatch = new Map<
    number,
    Array<Extract<WaterFall2RuntimeItem, { kind: 'product' }>>
  >();
  items.forEach((item) => {
    if (item.kind === 'similar') {
      const anchorItems = similarItemsByAnchor.get(item.anchorKey) || [];
      anchorItems.push(item);
      similarItemsByAnchor.set(item.anchorKey, anchorItems);
      return;
    }
    if (item.kind !== 'product') return;
    const batch = Math.floor(item.productIndex / WATERFALL2_PAGE_SIZE) + 1;
    const batchItems = productItemsByBatch.get(batch) || [];
    batchItems.push(item);
    productItemsByBatch.set(batch, batchItems);
  });

  const reflowKeys = new Set<string>();
  const fallbackTailItems = originalItemColumns.slice(-2);
  items.forEach((item) => {
    if (item.kind !== 'similar') return;
    const batchItems = item.restoreTrigger
      ? productItemsByBatch.get(item.restoreTrigger.batch) || []
      : [];
    const tailItems = batchItems.length > 0 ? batchItems.slice(-2) : fallbackTailItems;
    tailItems.forEach((tailItem) => reflowKeys.add(tailItem.key));
  });

  const layoutColumnHeights = new Array(columns).fill(0) as number[];
  const itemColumnMetaByKey = new Map(originalItemColumns.map((item) => [item.key, item]));
  items.forEach((item) => {
    if (item.kind === 'similar') return;
    const originalColumn = originalColumnByKey.get(item.key);
    if (originalColumn === undefined) return;
    const column =
      columns === 2 && reflowKeys.has(item.key)
        ? layoutColumnHeights.indexOf(Math.min(...layoutColumnHeights))
        : originalColumn;
    const y = layoutColumnHeights[column];

    positions.set(item.key, {
      column,
      x: column * (itemWidth + columnGap),
      y,
    });
    const itemColumnMeta = itemColumnMetaByKey.get(item.key);
    if (itemColumnMeta) itemColumnMeta.column = column;
    layoutColumnHeights[column] = y + (itemHeights[item.key] || defaultItemHeight) + itemGap;

    similarItemsByAnchor.get(item.key)?.forEach((similarItem) => {
      const similarY = layoutColumnHeights[column];
      positions.set(similarItem.key, {
        column,
        x: column * (itemWidth + columnGap),
        y: similarY,
      });
      layoutColumnHeights[column] =
        similarY + (itemHeights[similarItem.key] || defaultItemHeight) + itemGap;
    });
  });

  let height = 0;
  positions.forEach((position, key) => {
    height = Math.max(height, position.y + (itemHeights[key] || defaultItemHeight));
  });

  return { positions, height, originalItemColumns };
};
